/**
 * PlayServ SDK client factory for the Starblast demo.
 *
 * Reads connection parameters from Vite env vars (see `.env.example`),
 * creates a fresh `PlayServClient`, and applies them via `client.configure()`.
 * The created instance is cached so scenes can pick it up via `getClient()`
 * without threading it through Phaser scene data.
 *
 * Lifecycle: call `buildPlayServClient()` once from `client/main.ts` before
 * registering scenes. `BootScene` then calls `client.ready()` to drive the
 * handshake → ClientSettings → active flow.
 */

import { PlayServClient } from 'playserv-sdk';

import { readGuestIdentity } from './identity';

let _client: PlayServClient | null = null;

export function buildPlayServClient(): PlayServClient {
  const endpoint = import.meta.env.VITE_PLAYSERV_ENDPOINT;
  const gameAccessToken = import.meta.env.VITE_PLAYSERV_GAME_ACCESS_TOKEN;
  const gameId = import.meta.env.VITE_PLAYSERV_GAME_ID;
  const gameVersion = import.meta.env.VITE_PLAYSERV_GAME_VERSION ?? '0.1.0';

  if (!endpoint || !gameAccessToken || !gameId) {
    throw new Error(
      'Missing PlayServ env vars. Copy .env.example to .env and fill in ' +
        'VITE_PLAYSERV_ENDPOINT, VITE_PLAYSERV_GAME_ID, ' +
        'VITE_PLAYSERV_GAME_ACCESS_TOKEN.',
    );
  }

  const { userId } = readGuestIdentity();

  _client = new PlayServClient();
  _client.configure({
    endpoint,
    gameId,
    gameAccessToken,
    userId,
    gameVersion,
    allowMultipleConnections: false,
  });

  return _client;
}

export function getClient(): PlayServClient {
  if (_client === null) {
    throw new Error(
      'PlayServ client not initialized. Call buildPlayServClient() first.',
    );
  }
  return _client;
}
